import type { TierIIFacilityInfo } from '../types/facility-info.ts';
import type { FacilityMeta } from './tier2submit-export.ts';
/** Shared facility/contact projection for draft and submitted exports. */
export function tierIIFacilityMeta(info: TierIIFacilityInfo, facilityID: number): FacilityMeta {
  const contacts: NonNullable<FacilityMeta['contacts']> = [];
  if (info.facility.technicalContactName.trim()) {
    contacts.push({
      recordKey: 'owner-operator',
      name: info.facility.technicalContactName,
      nameIsBusiness: true,
      email: info.facility.technicalContactEmail,
      contactTypes: ['Owner / Operator'],
      mailingAddress: {
        street: info.facility.address,
        city: info.facility.city,
        state: info.facility.state,
        zip: info.facility.zip,
        country: 'USA',
      },
      phones: info.facility.technicalContactPhone
        ? [{ number: info.facility.technicalContactPhone, type: 'Work' }]
        : [],
    });
  }
  if (info.facility.parentCompanyName.trim()) {
    contacts.push({
      recordKey: 'parent-company',
      name: info.facility.parentCompanyName,
      nameIsBusiness: true,
      parentCompanyDunAndBradstreet: info.facility.parentCompanyDunBradstreetCode,
      contactTypes: ['Parent Company'],
      phones: [],
    });
  }
  if (info.tierIIContactName.trim()) {
    contacts.push({
      recordKey: 'tierii-contact',
      name: info.tierIIContactName,
      title: info.tierIIContactTitle,
      email: info.tierIIContactEmail,
      contactTypes: ['Tier II Information Contact'],
      phones: info.tierIIContactPhone ? [{ number: info.tierIIContactPhone, type: 'Work' }] : [],
    });
  }
  contacts.push(
    ...info.emergencyContacts
      .filter((contact) => contact.name.trim())
      .map((contact) => ({
        ...(contact.id ? { recordKey: contact.id } : {}),
        name: contact.name,
        title: contact.title,
        email: contact.email,
        contactTypes: [
          ...(contact.isEmergencyContact !== false ? ['Emergency Contact' as const] : []),
          ...(contact.isFacilityEmergencyCoordinator ? ['Fac. Emergency Coordinator' as const] : []),
          ...(contact.isEmergencyEquipmentContact ? ['Emergency Equipment Contact' as const] : []),
        ],
        phones: [
          ...(contact.phone ? [{ number: contact.phone, type: 'Emergency' as const }] : []),
          ...(contact.phone24 ? [{ number: contact.phone24, type: '24-hour' as const }] : []),
        ],
      })),
  );

  return {
    ...(info.stateReporting ? { stateReporting: info.stateReporting } : {}),
    jurisdictionProfile: info.jurisdictionProfile ?? null,
    facilityID: facilityID,
    name: info.facility.properName || info.facility.name,
    address: info.facility.address,
    city: info.facility.city,
    county: info.stateReporting?.facilityOverrides?.[info.facility.state]?.county ?? info.facility.county,
    state: info.facility.state,
    zip: info.facility.zip,
    mailingAddress: info.facility.mailingAddress,
    mailingCity: info.facility.mailingCity,
    mailingState: info.facility.mailingState,
    mailingZip: info.facility.mailingZip,
    fireDistrict: info.fireDistrict,
    department: info.department,
    latitude: info.latitude,
    longitude: info.longitude,
    emergencyPhone: info.emergencyPhone,
    rmpFacilityID: info.rmpFacilityID,
    certifierName: info.facility.certifierName,
    certifierTitle: info.facility.certifierTitle,
    dateSigned: info.dateSigned,
    naicsCodes: info.facility.naicsCodes,
    dunBradstreetCodes: info.facility.dunBradstreetCodes,
    triID: info.facility.triID || null,
    occupancyStatus: info.occupancyStatus,
    maxOccupants: info.maxOccupants,
    subjectToSection302: info.subjectToSection302,
    subjectToSection112r: info.subjectToSection112r,
    contacts,
  };
}
